import { AxiosResponse } from 'axios';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from './useApi';
import useErrorLog from './useErrorLog';
import useStoreStore from 'zustands/useStoreStore';
import useLogout from './useLogout';
import { useSelector } from 'react-redux';
import useUserStore from 'zustands/useUserStore';

export type StepPayCustomers = { ps_no: number; ps_sub_code: string; ps_sub_id: number };

export const useSubscribeCustomers = () => {
  const { setModalState } = useAlertConfirmStore();
  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [customerData, setCustomerData] = useState<StepPayCustomers>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const applySubscribeCustomers = async () => {
    setIsLoading((state) => !state);
    try {
      setLoadingState({ isFetchLoading: true });
      const { data, status }: AxiosResponse['data'] = await webApiNode('get', `/auth/getCustomer`);
      console.log(`/auth/customer_data`, data);
      if (status === 200) {
        setCustomerData({
          ps_no: data.resultData[0].ps_no,
          ps_sub_code: data.resultData[0].ps_sub_code,
          ps_sub_id: data.resultData[0].ps_sub_id,
        });
        return true;
      } else if (status === 409) {
        throw new Error('이미 등록되었습니다.');
      } else {
        throw new Error('알 수 없는 에러');
      }
    } catch (err) {
      setLoadingState({ isFetchLoading: false });
      checkErrorLog(err, 'useSubscribe applySubscribeCustomers');
      return setModalState({
        isModalShow: true,
        title: '',
        desc: `통신이 원활하지 않습니다`,
      });
    } finally {
    }
  };

  return { applySubscribeCustomers, customerData, isLoading };
};

export type OrderParams = {
  customerCode: string;
  customerId: number;
  productCode: string;
  priceCode: string;
};
export type OrderDataType = {
  orderCode: string;
  cancelUrl: string;
  errorUrl: string;
  successUrl: string;
};

export const useSubscribeOrders = () => {
  const { setModalState } = useAlertConfirmStore();
  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [orderData, setOrderData] = useState<OrderDataType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const applySubscribeOrders = async ({ productCode, priceCode, customerCode, customerId }: OrderParams) => {
    try {
      setIsLoading((state) => !state);
      const { data, status }: AxiosResponse['data'] = await webApiNode('post', `/auth/createOrder`, {
        productCode,
        priceCode,
        customerCode, //고객 코드
        customerId, //고객 ID
      });
      console.log('/auth/createOrder_data', data);
      if (status === 200) {
        setOrderData({
          orderCode: data.orderCode,
          cancelUrl: data.cancelUrl,
          errorUrl: data.errorUrl,
          successUrl: data.successUrl,
        });
      } else if (status === 409) {
        throw new Error('이미 등록되었습니다.');
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/createOrder');
    } finally {
      setIsLoading((state) => !state);
      setLoadingState({ isFetchLoading: false });
    }
  };

  return { applySubscribeOrders, orderData, isLoading };
};

export type PlanType = {
  ppp_code: string;
  ppp_month: number;
  ppp_origin_price: number;
  ppp_discount_price: number;
  ppp_discount_rate: number;
};

export type ProductType = {
  pp_no: number;
  pp_code: string;
  pp_type: 'NORMAL' | 'EVENT';
  pp_name: string;
  pp_summary: string;
  pp_description: string;
  pp_plan_list: PlanType[];
};

export const useSubscribePlans = () => {
  const { setModalState } = useAlertConfirmStore();
  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [products, setProducts] = useState<ProductType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getSubscribePlans = async (TYPE: 'basic' | 'event' = 'basic') => {
    setIsLoading((state) => !state);
    try {
      setLoadingState({ isFetchLoading: true });
      const { data, status }: AxiosResponse['data'] = await webApiNode(
        'post',
        `/auth/productList?product_type=${TYPE}`,
      );
      console.log(`/auth/productList?product_type=${TYPE}_data`, data);
      console.log(`/auth/productList?product_type=${TYPE}_status`, status);

      if (status === 200) {
        // JSON.parse()를 이용하여 pp_summary 배열을 파싱
        const _products: ProductType[] = data.map((product: ProductType) => {
          return {
            ...product,
            pp_summary: JSON.parse(product.pp_summary),
          };
        });

        setProducts(_products);
        return true;
      }
    } catch (err) {
      checkErrorLog(err, `useSubscribe.ts  /auth/productList?product_type=${TYPE}`);
    } finally {
      setIsLoading((state) => !state);
      setLoadingState({ isFetchLoading: false });
    }
  };

  return { getSubscribePlans, products, isLoading };
};

export type StatusType = {
  pp_name: string;
  rss_status: 'ACTIVE' | 'INACTIVE' | 'PAUSE' | 'CANCEL' | 'CANCEL_WAIT';
  rss_start_date: string;
  rss_end_date: string;
  rss_next_date: string;
  rss_subscription_id?: number;
  ppp_discount_price?: number;
  rss_created_at?: string;
};

export const useSubscribeStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ISP } = useUserStore();

  const { setModalState } = useAlertConfirmStore();
  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [status, setStatus] = useState<StatusType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getSubscribeStatus = async () => {
    try {
      setIsLoading((state) => !state);
      const { data }: AxiosResponse['data'] = await webApiNode('get', `/auth/checkInfo`);
      console.log('/auth/checkInfo_data', data);

      if (data.resultCode === 0) {
        setStatus(data.resultData);
        // 요금제 페이지 제외
        const excludedScreen = ['/login', '/payment', '/help', '/payment-info', '/business-establishment-choice'];
        if (!ISP && !excludedScreen.includes(location.pathname)) {
          // 비즈니스 구독권 체크
          if (!data?.resultData[0]?.pp_name.includes('비즈니스')) {
            setModalState({
              isModalShow: true,
              title: '비즈니스 구독권 미보유',
              desc: '샵솔 PC 버전은 비즈니스 플랜을 구독하신 뒤 이용하실 수 있습니다.',
              okBtnText: '구독권 보기',
              okCallback: () => {
                navigate('/payment');
              },
            });
            return navigate(`/business-establishment-choice`);
          }
          // 비즈니스지만 활성화 또는 구독 취소예정 상태가 아닌경우
          if (data?.resultData[0]?.rss_status !== 'ACTIVE' && data?.resultData[0].rss_status !== 'CANCEL_WAIT') {
            setModalState({
              isModalShow: true,
              title: '비즈니스 구독권 만료',
              desc: '이용중인 구독권이 만료되었습니다.',
              okBtnText: '구독권 보기',
              okCallback: () => {
                navigate('/payment');
              },
            });
            return navigate(`/business-establishment-choice`);
          }
        }
      } else {
        throw new Error(data.resultMsg);
      }
      return true;
    } catch (err) {
      checkErrorLog(err, `getSubscribeStatus/subscription/checkInfo`);
    } finally {
      setIsLoading((state) => !state);
    }
  };

  return { getSubscribeStatus, status, isLoading };
};
